import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, FormControlLabel, Stack, Switch, TextField, Typography } from '@mui/material';
import RouteIcon from '@mui/icons-material/Route';
import { toast } from 'react-toastify';
import { useAuth } from '../services/AuthService';

export default function DeliveryPricing() {
  const { api } = useAuth();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get('/delivery-pricing/settings/admin');
    setSettings({ enabled: true, base_fee: 4, per_km_fee: 0.5, ...data });
  }, [api]);

  useEffect(() => {
    load().catch((error) => toast.error(error.response?.data?.error || 'Não foi possível carregar o frete por KM.'));
  }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/delivery-pricing/settings/admin', {
        enabled: Boolean(settings.enabled),
        base_fee: Number(settings.base_fee),
        per_km_fee: Number(settings.per_km_fee)
      });
      setSettings(data.settings);
      toast.success('Regra de frete por KM salva.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Não foi possível salvar a regra do frete.');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 850, mx: 'auto' }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Box sx={{ width: 52, height: 52, borderRadius: 3, bgcolor: '#eff6ff', color: '#2563eb', display: 'grid', placeItems: 'center' }}><RouteIcon /></Box>
        <Box>
          <Typography variant="h4" fontWeight={900}>Frete do aplicativo por KM</Typography>
          <Typography color="text.secondary">O preço é calculado no servidor para pedidos com entrega do All Delivery.</Typography>
        </Box>
      </Stack>
      <Alert severity="info" sx={{ mb: 3 }}>
        A regra inicial é R$ 4,00 de base + R$ 0,50 por KM. O sistema calcula distância aproximada em linha reta entre a lanchonete e o GPS do cliente; se faltar uma das localizações, cobra somente a taxa base.
      </Alert>
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack spacing={2.5}>
            <FormControlLabel control={<Switch checked={Boolean(settings.enabled)} onChange={(event) => setSettings((current) => ({ ...current, enabled: event.target.checked }))} />} label="Ativar acréscimo por KM nas entregas do aplicativo" />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField fullWidth type="number" label="Taxa base (R$)" value={settings.base_fee} onChange={(event) => setSettings((current) => ({ ...current, base_fee: event.target.value }))} inputProps={{ min: 0, step: 0.01 }} />
              <TextField fullWidth type="number" label="Valor por KM (R$)" value={settings.per_km_fee} onChange={(event) => setSettings((current) => ({ ...current, per_km_fee: event.target.value }))} inputProps={{ min: 0, step: 0.01 }} />
            </Stack>
            <Typography variant="body2" color="text.secondary">Exemplo atual: 1 km = R$ 4,50; 5 km = R$ 6,50. A taxa é proporcional à distância e arredondada para centavos.</Typography>
            <Button variant="contained" size="large" onClick={save} disabled={saving}>{saving ? 'Salvando...' : 'Salvar regra do frete'}</Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
